import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Shipment {
  id: string
  tracking_number: string
  order_id: string
  carrier_name: string
  service: string
  status: string
  origin: { city: string; country: string }
  destination: { city: string; country: string }
  weight_kg: number
  shipping_cost: number
  estimated_delivery: string
  shipped_at: string
}

interface Carrier {
  id: string
  name: string
  full_name: string
  regions: string[]
  services: string[]
  status: string
  shipments_today: number
  on_time_rate: number
  avg_delivery_days: number
}

export default function ShippingLogisticsPage() {
  const [shipments, setShipments] = useState<Shipment[]>([])
  const [carriers, setCarriers] = useState<Carrier[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [trackingNumber, setTrackingNumber] = useState('')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [shipmentsRes, carriersRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/shipping/shipments'),
      fetch(API_BASE + '/shipping/carriers'),
      fetch(API_BASE + '/shipping/statistics')
    ])
    if (shipmentsRes.ok) setShipments((await shipmentsRes.json()).shipments || [])
    if (carriersRes.ok) setCarriers((await carriersRes.json()).carriers || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-800'
      case 'out_for_delivery': return 'bg-blue-100 text-blue-800'
      case 'in_transit': return 'bg-indigo-100 text-indigo-800'
      case 'picked_up': return 'bg-yellow-100 text-yellow-800'
      case 'label_created': return 'bg-gray-100 text-gray-800'
      case 'exception': return 'bg-red-100 text-red-800'
      case 'returned': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Shipping & Logistics</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Shipment</button>
        </div>

        <div className="bg-white rounded-lg shadow p-4 mb-8">
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Enter tracking number..."
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Track
            </button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Shipped Today</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.shipped_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">In Transit</div>
              <div className="text-2xl font-bold text-indigo-600">{statistics.in_transit}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Delivered</div>
              <div className="text-2xl font-bold text-green-600">{statistics.delivered}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">On-Time Rate</div>
              <div className="text-2xl font-bold">{statistics.on_time_delivery_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {carriers.slice(0, 4).map(carrier => (
            <div key={carrier.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="font-semibold">{carrier.name}</div>
                <span className={`px-2 py-0.5 rounded text-xs ${carrier.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {carrier.status}
                </span>
              </div>
              <div className="text-2xl font-bold text-blue-600 mb-2">{carrier.shipments_today}</div>
              <div className="text-sm text-gray-500 mb-2">shipments today</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-500">On-time: </span>
                  <span className="font-medium text-green-600">{carrier.on_time_rate}%</span>
                </div>
                <div>
                  <span className="text-gray-500">Avg: </span>
                  <span className="font-medium">{carrier.avg_delivery_days}d</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Shipments</h2></div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tracking</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Carrier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est. Delivery</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {shipments.slice(0, 12).map(shipment => (
                <tr key={shipment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-mono text-sm">{shipment.tracking_number}</div>
                    <div className="text-xs text-gray-500">{shipment.order_id}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      {shipment.origin.city}, {shipment.origin.country}
                    </div>
                    <div className="text-xs text-gray-500">
                      → {shipment.destination.city}, {shipment.destination.country}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium">{shipment.carrier_name}</div>
                    <div className="text-xs text-gray-500 capitalize">{shipment.service}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">{new Date(shipment.estimated_delivery).toLocaleDateString()}</div>
                    <div className="text-xs text-gray-500">{shipment.weight_kg} kg • {formatCurrency(shipment.shipping_cost)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(shipment.status)}`}>
                      {shipment.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}
